<template>
  <section id="platform" class="pillars" aria-labelledby="pillars-title">
    <div class="pillars-bg" aria-hidden="true"></div>

    <div class="pillars-inner">
      <header class="pillars-head" data-reveal="up">
        <span class="pillars-eyebrow">{{ $t('landing.pillars_label') }}</span>
        <h2 id="pillars-title" class="pillars-title">
          {{ $t('landing.pillars_title_pre') }}
          <span class="grad">{{ $t('landing.pillars_title_highlight') }}</span>
        </h2>
        <p class="pillars-sub">{{ $t('landing.pillars_subtitle') }}</p>
      </header>

      <div class="pillars-grid">
        <article
          v-for="(p, i) in pillars"
          :key="p.key"
          class="pillar"
          :class="`accent-${p.accent}`"
          data-reveal="up"
          :data-reveal-delay="i + 1"
        >
          <span class="pillar-step">{{ String(i + 1).padStart(2, '0') }}</span>
          <span class="pillar-icon">
            <NuxtIcon :name="p.icon" />
          </span>
          <h3 class="pillar-title">{{ $t(`landing.pillar_${p.key}_title`) }}</h3>
          <p class="pillar-desc">{{ $t(`landing.pillar_${p.key}_desc`) }}</p>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup>
// Six pillars = the full journey a release travels through the platform, in
// order. Accent alternates cyan/coral so the grid reads as a rhythm rather
// than a flat list. Icons are mdi (already bundled via NuxtIcon elsewhere).
const pillars = [
  { key: 'distribute', icon: 'mdi:earth', accent: 'cyan' },
  { key: 'promote', icon: 'mdi:bullhorn-variant-outline', accent: 'coral' },
  { key: 'sell', icon: 'mdi:cart-outline', accent: 'cyan' },
  { key: 'getpaid', icon: 'mdi:cash-multiple', accent: 'coral' },
  { key: 'create', icon: 'mdi:auto-fix', accent: 'cyan' },
  { key: 'scale', icon: 'mdi:account-group-outline', accent: 'coral' }
]
</script>

<style scoped>
/* Mobile-first. Ladder: 480 / 640 / 768 / 1024 / 1280 (variables.css). */
.pillars {
  position: relative;
  padding: var(--landing-section-pad) 0;
  overflow: hidden;
}
.pillars-bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 70% 60% at 50% 0%, rgb(var(--primary-channels) / 0.05), transparent 65%);
  pointer-events: none;
}
.pillars-inner {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.pillars-head {
  text-align: center;
  max-width: 720px;
  margin: 0 auto 64px;
}
.pillars-eyebrow {
  display: inline-block;
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--primary);
  margin-bottom: 16px;
}
.pillars-title {
  font-family: var(--font-heading);
  font-size: clamp(2rem, 4.4vw, 3.2rem);
  font-weight: 800;
  line-height: 1.05;
  text-transform: uppercase;
  letter-spacing: -0.01em;
  margin: 0 0 16px;
}
.grad {
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.pillars-sub {
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--text-muted);
  margin: 0;
}

.pillars-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

@media (min-width: 640px) {
  .pillars-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 18px;
  }
}

@media (min-width: 1024px) {
  .pillars-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.pillar {
  position: relative;
  padding: clamp(24px, 2.4vw + 16px, 34px) clamp(20px, 2vw + 14px, 30px) clamp(22px, 2vw + 15px, 30px);
  border-radius: var(--radius-xl);
  border: 1px solid var(--border);
  background: var(--surface-color);
  overflow: hidden;
  transition: border-color 0.28s ease, transform 0.28s ease, box-shadow 0.28s ease;
}
.pillar::before {
  content: '';
  position: absolute;
  inset: 0 0 auto 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--accent-col), transparent);
  opacity: 0;
  transition: opacity 0.28s ease;
}
.pillar:hover {
  transform: translateY(-4px);
  border-color: color-mix(in srgb, var(--accent-col) 45%, transparent);
  box-shadow: 0 20px 50px -24px var(--accent-col);
}
.pillar:hover::before { opacity: 1; }

.accent-cyan { --accent-col: var(--primary); }
.accent-coral { --accent-col: var(--secondary); }

.pillar-step {
  position: absolute;
  top: 24px;
  right: 26px;
  font-family: var(--font-heading);
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--text-dim);
  opacity: 0.5;
}
.pillar-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  margin-bottom: 20px;
  font-size: 1.5rem;
  color: var(--accent-col);
  background: color-mix(in srgb, var(--accent-col) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent-col) 30%, transparent);
}
.pillar-title {
  font-family: var(--font-heading);
  font-size: 1.25rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  margin: 0 0 10px;
}
.pillar-desc {
  font-size: 0.92rem;
  line-height: 1.6;
  color: var(--text-muted);
  margin: 0;
}

</style>
